import { Pipe, PipeTransform } from "@angular/core";
import { Currency } from "@models/interfaces/currency";
import { round } from "../util/component-util";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Helper } from "@helpers/functions";
import { getCurrentDecimalSeperator, getLocaleArray } from "@helpers/utils";
import { TRADABLE_PRECISION } from "@modules/staking/domain/entities/constants";
import { Tradable } from "@models/interfaces/tradable";

export abstract class CurrencyPrecisionPipe {
	public abstract transform(val: string | number, args: PrecisionInterface): string;
}

@Pipe({
	name: "tradable_precision",
	standalone: false
})
export class TradablePrecisionPipe extends CurrencyPrecisionPipe implements PipeTransform {
	public constructor(private tradableListService: TradableListService) {
		super();
	}

	public transform(val: string | number, args: PrecisionInterface = new TradablePrecisionOptions()): string {
		// Public transform(val: string | number, id: string = null, ignorePrecision: boolean = false, txPrecision: boolean = false): string {
		let value = 0;
		if (typeof val === "string") {
			value = +val;
		} else {
			value = val;
		}

		if ((args.id === undefined || args.id === null) && (args.assetPid === undefined || args.assetPid === null)) {
			return round(+value, 8);
		}

		let coin: Tradable;
		if (args.assetPid) {
			coin = this.tradableListService.getItemByPid(args.assetPid);
		} else {
			coin = this.tradableListService.getItemById(args.id);
		}

		if (coin) {
			let precision = coin.getPrecision();
			if (args.removePrecision) {
				precision = 0;
			} else if (args.txPrecision) {
				precision = coin.getPrecisionForTx();
			}

			if (value < Math.pow(10, -precision)) {
				precision = coin.getPrecisionForTx();
			}

			if (args.customPrecision) {
				precision = args.customPrecision;
			}

			const str = round(+value, precision, args.ignorePrecision || value < args.dustThreshold);

			if (args.noTrailingZeros) {
				// remove trailing zeros from string
				const regexPattern = getCurrentDecimalSeperator() === "." ? /\.?0+$/ : /,?0+$/;
				return str.replace(regexPattern, "");
			}
			return str;
		}

		return value + "";
	}
}

@Pipe({
	name: "four_eight_tradable_precision",
	standalone: false
})
export class FourEightTradablePrecisionPipe implements PipeTransform {
	public transform(val: string | number): string {
		let value = 0;
		if (val) {
			if (typeof val === "string") {
				value = +val;
			} else {
				value = val;
			}

			const displayEightDecimals: boolean = Math.floor(value) === 0 && value.toFixed(8).slice(2, 6) === "0000";
			let precision = 0;
			if (displayEightDecimals) {
				precision = 8;
			} else {
				precision = 4;
			}

			return round(value, precision);
		}
	}
}

@Pipe({
	name: "fiat_precision",
	standalone: false
})
export class FiatPrecisionPipe extends CurrencyPrecisionPipe implements PipeTransform {
	public transform(val: string | number, args: PrecisionInterface = new FiatPrecisionOptions()): string {
		let value = 0;
		if (typeof val === "string") {
			value = +val;
		} else {
			value = val;
		}

		if (args.omitZero && value === 0) {
			return "-";
		}

		if (!!args.currencies && args.currencies.length > 0) {
			let precision = Math.max(2, ...args.currencies.map((c) => (!c ? 0 : c.getPrecisionFiat())));
			if (args.removePrecision) {
				precision = 0;
			}

			if (args.customPrecision) {
				precision = args.customPrecision;
			}

			if (args.exponentPrecision) {
				value =
					Math.trunc(+value / Math.pow(10, args.exponentPrecision)) * Math.pow(10, args.exponentPrecision);
			}

			let rounded = round(+value, precision);
			if (value < Math.pow(10, precision * -1) && args.prefix) {
				if (value >= 0 && args.tradableAmount > 0) {
					rounded = rounded.substring(0, rounded.length - 1) + "1";
				}
			} else if (args.prefix && !args.notPrice) {
				rounded = "~" + rounded;
			}
			if (args.cutZeroSuffix) {
				let i = 0;
				const cutDecimals = args.cutAllZeroSuffix ? 3 : 2;

				// Leave at least 2 decimals
				while (rounded.lastIndexOf("0") === rounded.length - 1 && i++ < cutDecimals) {
					rounded = rounded.substr(0, rounded.length - 1);
				}
			}
			if (args.showPositivePrefix) {
				return "+" + rounded;
			}
			return rounded;
		}
		return round(+value, 2);
	}
}

@Pipe({
	name: "local_format",
	standalone: false
})
export class LocalFormatPipe implements PipeTransform {
	public transform(val: string | number, displayTradable: boolean = true): string {
		const locales: string[] = getLocaleArray(Helper.getLanguage());
		const value = (+val).toLocaleString(locales, {
			localeMatcher: "best fit",
			minimumIntegerDigits: 1,
			minimumFractionDigits: 0,
			maximumFractionDigits: displayTradable ? TRADABLE_PRECISION : 2
		});
		return value;
	}
}

@Pipe({
	name: "fiat_locale_format",
	standalone: false
})
export class FiatLocaleFormatPipe implements PipeTransform {
	public transform(val: string | number, precision: number): string {
		const locales: string[] = getLocaleArray(Helper.getLanguage());
		const value = (+val).toLocaleString(locales, {
			localeMatcher: "best fit",
			minimumIntegerDigits: 1,
			minimumFractionDigits: 0,
			maximumFractionDigits: precision
		});
		// if value is 0 then reset input by assign value to null
		return value === "0" ? null : value;
	}
}

// this is an alternative helper function to convert any 0 or "0" to null for the "local_format" pipe
@Pipe({
	name: "reset_zero_to_null",
	standalone: false
})
export class LocalFormatResetPipe implements PipeTransform {
	transform(value: string | number): string | number | null {
		if (value === 0 || value === "0") {
			return null;
		}

		return value;
	}
}

export interface PrecisionInterface {
	prefix?: boolean;
	id?: string;
	notPrice?: boolean;
	currencies?: Currency[];
	ignorePrecision?: boolean;
	removePrecision?: boolean;
	removeCurrency?: boolean;
	exponentPrecision?: number;
	txPrecision?: boolean;
	lowercase?: boolean;
	cutZeroSuffix?: boolean;
	cutAllZeroSuffix?: boolean;
	symbol?: boolean;
	omitZero?: boolean;
	addName?: boolean;
	largeNumber?: boolean;
	round?: boolean;
	valueFirst?: boolean;
	showPositivePrefix?: boolean;
	dustThreshold?: number;
	customPrecision?: number;
	onlyUnit?: boolean;
	noTrailingZeros?: boolean;
	tradableAmount?: number;
	showNameInsteadOfSymbol?: boolean;
	showGrams?: boolean;
	isDust?: boolean;
	assetPid?: string;
}

class TradablePrecisionOptions implements PrecisionInterface {
	public id: string = null;
	public ignorePrecision: boolean = false;
	public txPrecision: boolean = false;
	public dustThreshold: number = 0;
}

export class FiatPrecisionOptions implements PrecisionInterface {
	public id: string = "1";
	public currencies: Currency[] = [];
	public ignorePrecision: boolean = false;
	public removePrecision: boolean = false;
	public removeCurrency: boolean = false;
	public exponentPrecision: number = null;
	public lowercase: boolean = false;
	public cutZeroSuffix: boolean = false;
	public symbol: boolean = false;
	public prefix: boolean = false;
}
